/**
 * Button - Base button component
 *
 * Unified button with variants for consistent styling across the app.
 */

import type { ReactNode, ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { formStyles, mergeStyles } from '../formStyles';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Button content */
  children: ReactNode;
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Icon to display before text */
  icon?: LucideIcon;
  /** Icon to display after text */
  iconRight?: LucideIcon;
  /** Loading state */
  loading?: boolean;
  /** Full width button */
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, typeof formStyles.buttonPrimary> = {
  primary: formStyles.buttonPrimary,
  secondary: formStyles.buttonSecondary,
  danger: formStyles.buttonDanger,
  success: formStyles.buttonSuccess,
  ghost: formStyles.buttonGhost,
};

const sizeStyles: Record<ButtonSize, typeof formStyles.buttonSmall | undefined> = {
  small: formStyles.buttonSmall,
  medium: undefined,
  large: formStyles.buttonLarge,
};

export function Button({
  children,
  variant = 'primary',
  size = 'medium',
  icon: Icon,
  iconRight: IconRight,
  loading,
  fullWidth,
  disabled,
  style,
  ...props
}: ButtonProps) {
  const buttonStyle = mergeStyles(
    formStyles.button,
    variantStyles[variant],
    sizeStyles[size],
    (disabled || loading) && formStyles.buttonDisabled,
    fullWidth && { width: '100%' },
    style
  );

  const iconSize = size === 'small' ? 14 : size === 'large' ? 18 : 16;

  return (
    <button
      {...props}
      disabled={disabled || loading}
      style={buttonStyle}
    >
      {loading ? (
        <span
          style={{
            width: iconSize,
            height: iconSize,
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }}
        />
      ) : Icon ? (
        <Icon size={iconSize} />
      ) : null}
      {children}
      {IconRight && !loading && <IconRight size={iconSize} />}
    </button>
  );
}
